// Phase JB.3b — Phantom Bet card.
//
// Anon-only — hidden when the user is authenticated. Lets a visitor try
// a slip without signing up. Outcome is settled by jb-phantom-settle,
// which uses _shared/settlement.ts. N01/N03: outcome is NEVER rigged.
//
// Composes existing I0 primitives only. No primitive edits.

import { useMemo, type CSSProperties } from "react";
import { Badge, Countdown, Eyebrow, Pill } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { usePhantomBet, MAX_PHANTOM_LEGS } from "../../conversion/usePhantomBet.ts";
import { useDailyAccaSignals } from "../../free-tier/useDailyAccaSignals.ts";
import { formatLeg } from "../../exchange/market-format.ts";

const fontBody = "var(--sa-font-body)";
const fontMono = "var(--sa-font-mono)";

const wrapperStyle: CSSProperties = { padding: "12px 48px 8px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

export interface PhantomBetCardProps {
  /** Test override: force display even when authed. */
  forceShow?: boolean;
}

export function PhantomBetCard({ forceShow }: PhantomBetCardProps = {}): JSX.Element | null {
  const { user } = useAuthSession();
  const { draft, locked, loading, error, addLeg, removeLeg, lockPhantom } = usePhantomBet();
  const { signals } = useDailyAccaSignals();
  const published = useMemo(
    () => signals.filter((s) => s.published_at !== null).slice(0, 8),
    [signals],
  );

  // Anon-only — N18 doctrine: this is an onboarding sandbox, not a
  // perpetual free service for paying users.
  if (user && forceShow !== true) return null;

  // Settled state.
  if (locked && locked.outcome !== null && locked.outcome !== "expired") {
    const outcomeWord =
      locked.outcome === "won" ? "WON" : locked.outcome === "lost" ? "LOST" : "VOID";
    const variant =
      locked.outcome === "won" ? "success" : locked.outcome === "lost" ? "danger" : "ghost";
    return (
      <section aria-label="Phantom Bet" data-testid="phantom-card-settled" style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>Phantom slip · Settled</Eyebrow>
            <Badge variant={variant}>{outcomeWord}</Badge>
          </div>
          <p style={{ fontFamily: fontBody, fontSize: 13.5, color: "var(--sa-fg-secondary)", marginTop: 12, lineHeight: 1.7 }}>
            This is the real outcome — the same settlement engine STRIKZA uses for paying users. Nothing rigged, nothing simulated.
          </p>
        </div>
      </section>
    );
  }

  // Locked, pending settlement.
  if (locked && locked.outcome === null) {
    return (
      <section aria-label="Phantom Bet" data-testid="phantom-card-locked" style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>Phantom slip · Awaiting result</Eyebrow>
            <Pill>SETTLES IN</Pill>
          </div>
          <div style={{ marginTop: 14 }}>
            <Countdown
              targetISO={new Date(new Date(locked.locked_at).getTime() + 5 * 60 * 1000).toISOString()}
              compact
            />
          </div>
          <p style={{ fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-muted)", marginTop: 12 }}>
            Settles on the same logic as real signals — what you see here is what would have happened.
          </p>
        </div>
      </section>
    );
  }

  // Building state.
  return (
    <section aria-label="Phantom Bet" data-testid="phantom-card-empty" style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Phantom slip · Try without signing up</Eyebrow>
          <Pill>NO ACCOUNT NEEDED</Pill>
        </div>
        <p style={{ fontFamily: fontBody, fontSize: 13.5, color: "var(--sa-fg-secondary)", marginTop: 12, lineHeight: 1.7 }}>
          Build a slip of up to {MAX_PHANTOM_LEGS} legs. Phantom bets settle on the same logic as real signals — what you see here is what would have happened.
        </p>

        {published.length === 0 ? (
          <p style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-fg-muted)", marginTop: 14, letterSpacing: "0.18em" }}>
            NO PUBLISHED SIGNALS YET — CHECK BACK TODAY
          </p>
        ) : (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {published.map((s) => {
              const inDraft = draft.legs.some((l) => l.signal_id === s.signal_id);
              const disabled = inDraft || draft.legs.length >= MAX_PHANTOM_LEGS;
              return (
                <div
                  key={s.signal_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid var(--sa-hairline)",
                    padding: "8px 0",
                    gap: 12,
                  }}
                >
                  <span style={{ fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-primary)" }}>
                    {s.fixture_name} · {formatLeg(s.market, s.selection)}
                  </span>
                  <button
                    data-testid={`phantom-add-${s.signal_id}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => addLeg({
                      signal_id: s.signal_id,
                      market: s.market,
                      selection: s.selection,
                      match_id: s.fixture_id ?? undefined,
                      odds: s.odds,
                    })}
                    style={{
                      fontFamily: fontMono,
                      fontSize: 10.5,
                      letterSpacing: "0.18em",
                      padding: "6px 10px",
                      borderRadius: 4,
                      border: "1px solid var(--sa-hairline)",
                      background: "transparent",
                      color: disabled ? "var(--sa-fg-muted)" : "var(--sa-fg-primary)",
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    {inDraft ? "ADDED" : "ADD"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {draft.legs.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontFamily: fontMono, fontSize: 10.5, letterSpacing: "0.18em", color: "var(--sa-fg-muted)", marginBottom: 8 }}>
              YOUR DRAFT · {draft.legs.length} LEG{draft.legs.length === 1 ? "" : "S"}
            </div>
            {draft.legs.map((l) => (
              <div key={l.leg_id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                <span style={{ fontFamily: fontBody, fontSize: 12.5, color: "var(--sa-fg-secondary)" }}>
                  {formatLeg(l.market, l.selection)}
                </span>
                <button
                  type="button"
                  onClick={() => removeLeg(l.leg_id)}
                  style={{ background: "none", border: "none", color: "var(--sa-fg-muted)", cursor: "pointer", fontFamily: fontMono, fontSize: 10 }}
                >
                  REMOVE
                </button>
              </div>
            ))}
            <button
              data-testid="phantom-lock"
              type="button"
              onClick={() => void lockPhantom()}
              disabled={loading}
              style={{
                marginTop: 12,
                fontFamily: fontMono,
                fontSize: 11,
                letterSpacing: "0.18em",
                padding: "10px 18px",
                borderRadius: 4,
                border: "none",
                background: "var(--sa-accent-saffron)",
                color: "var(--sa-bg-base)",
                cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "LOCKING…" : "LOCK PHANTOM SLIP"}
            </button>
            {error && (
              <div style={{ marginTop: 8, fontFamily: fontMono, fontSize: 11, color: "var(--sa-signal-red)" }}>
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
